<template>
    <div v-if="loaded">
        <div v-if="isAdmin" style="margin-bottom:12px;font-size:13px">
            <template v-if="viewAsTarget">
                <span style="color:#a8a4a0">Viewing as</span>
                <b style="margin:0 6px">{{ viewAsTarget.name }}</b>
                <a @click.prevent="stopViewingAs()" class="primary" style="text-decoration:underline">Stop viewing as</a>
            </template>
            <a v-else @click.prevent="showViewAsPicker = true" class="primary" style="text-decoration:underline">View as another user</a>
        </div>

        <div class="chk-week">
            <div v-for="(day, i) in weekDays" :key="i" @click="selectDay(day.dateString)" class="chk-day" :class="{ on: day.dateString == selectedDateString }">
                <span class="cd-dow">{{ day.dow }}</span>
                <span class="cd-num">{{ day.dateNum }}</span>
                <span class="cd-dot" :class="day.dotClass"></span>
            </div>
        </div>

        <div class="panel">
            <h4>{{ selectedDateLabel }}<span>{{ doneCount }}/{{ totalCount }}</span></h4>
            <div class="bar"><span :style="'width:' + progressPct + '%'"></span></div>

            <div v-if="viewAsTarget" style="font-size:12px;color:#024E73;background:#EAF1F5;border-radius:8px;padding:9px 12px;margin:8px 0 14px">
                Viewing {{ viewAsTarget.name }}'s checklist — you can add one-off items on their behalf here. Everything else is read-only.
            </div>
            <div v-else-if="isToday" style="font-size:12px;color:#a8a4a0;margin:8px 0 14px">
                Filling out as {{ $store.state.user.firstName }} {{ $store.state.user.lastName }}
            </div>
            <div v-else-if="isPast" style="font-size:12px;color:#a8a4a0;background:#F6F5F3;border-radius:8px;padding:9px 12px;margin:8px 0 14px">
                Read only — showing what was completed on {{ selectedDateLabel }}.
            </div>
            <div v-else style="font-size:12px;color:#a8a4a0;background:#F6F5F3;border-radius:8px;padding:9px 12px;margin:8px 0 14px">
                Upcoming — showing what's scheduled for {{ selectedDateLabel }}.
            </div>

            <div v-if="checklist.weeklyItems.length" class="chk-weekly">
                <div class="chk-weekly-head"><svg-icon icon="calendar" />Weekly Items</div>
                <div v-for="(item, i) in checklist.weeklyItems" :key="i">
                    <div v-if="item.type == 'textblock'" class="playbook-process checklist-text-block">
                        <div v-html="item.text"></div>
                    </div>
                    <div v-else @click="toggleItem(item)" class="chk-item" :class="{ on: item.checked, ro: readOnlyMode }">
                        <div>
                            <b>{{ item.label }}<span v-if="item.description">:</span></b>&nbsp;<span>{{ item.description }}</span>
                            <span v-if="item.carriedFromLabel" class="chk-missed">Missed {{ item.carriedFromLabel }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="checklist.oneOffItems.length || isToday || isFuture" class="chk-oneoff">
                <div class="chk-oneoff-head"><svg-icon icon="plus" style="width:14px;height:14px" />One-Off Items</div>
                <div v-for="(item, i) in checklist.oneOffItems" :key="i" class="chk-oneoff-row">
                    <div @click="toggleItem(item)" class="chk-item" :class="{ on: item.checked, ro: readOnlyMode }">
                        <div>
                            <b>{{ item.label }}</b>
                            <span v-if="item.carriedFromLabel" class="chk-missed">Missed {{ item.carriedFromLabel }}</span>
                            <span v-if="item.addedByName" class="chk-added-by">Added by {{ item.addedByName }}</span>
                        </div>
                    </div>
                    <svg-icon v-if="isToday && !viewAsTarget" @click="removeOneOffItem(item)" icon="x" class="chk-oneoff-remove" />
                </div>
                <div v-if="isToday || isFuture" class="chk-oneoff-add">
                    <input type="text" v-model="oneOffInput" @keyup.enter="addOneOffItem()" placeholder="Add a one-off item..." />
                    <div @click="addOneOffItem()" :class="oneOffInput.trim() ? 'btn' : 'btn disable-button'" style="width:auto;font-size:14px">Add</div>
                </div>
            </div>

            <div v-for="(item, i) in checklist.items" :key="i">
                <div v-if="(item.section && i == 0)" class="playbook-process checklist-text-block" style="margin: 20px 0"><b>{{ item.section }}</b></div>
                <div v-else-if="(i > 0 && item.section && checklist.items[i-1].section != item.section)" class="playbook-process checklist-text-block" style="margin: 20px 0"><b>{{ item.section }}</b></div>
                <div v-if="item.type == 'textblock'" class="playbook-process checklist-text-block" style="margin: 20px 0">
                    <div v-html="item.text"></div>
                </div>
                <div v-else @click="toggleItem(item)" class="chk-item" :class="{ on: item.checked, ro: readOnlyMode }">
                    <div>
                        <b>{{ item.label }}<span v-if="item.description">:</span></b>&nbsp;<span>{{ item.description }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="isAdmin && !viewAsTarget && adminHistoryEntries.length" class="panel">
            <h4>All Users' History</h4>
            <div class="page-filters">
                <input type="text" v-model="adminSearchQ" placeholder="Search by user name" />
            </div>
            <div v-if="!filteredAdminHistoryEntries.length" style="font-size:12px;color:#a8a4a0;padding:6px 0 10px">No matching entries.</div>
            <div v-for="(h, i) in filteredAdminHistoryEntries" :key="i" @click="showAdminEntry(h)" class="cl-card">
                <div class="cl-date">
                    <div class="cl-mon">{{ h.mon }}</div>
                    <div class="cl-day">{{ h.day }}</div>
                    <div class="cl-yr">{{ h.yr }}</div>
                </div>
                <div style="flex:1">
                    <div class="cl-nm">{{ h.userName }}</div>
                    <div class="cl-sub">{{ h.done }}/{{ h.total }} items completed</div>
                </div>
                <div class="cl-right">
                    <span :class="'pill ' + (h.total && h.done >= h.total ? 'p-done' : 'p-live')">
                        <svg-icon :icon="h.total && h.done >= h.total ? 'check2' : 'x'" />
                        {{ h.total && h.done >= h.total ? 'Complete' : 'Incomplete' }}
                    </span>
                </div>
            </div>
        </div>

        <div v-else-if="historyEntries.length" class="panel">
            <h4>{{ viewAsTarget ? viewAsTarget.name + "'s History" : 'My History' }}</h4>
            <div v-for="(h, i) in historyEntries" :key="i" @click="selectDay(h.dateString)" class="cl-card">
                <div class="cl-date">
                    <div class="cl-mon">{{ h.mon }}</div>
                    <div class="cl-day">{{ h.day }}</div>
                    <div class="cl-yr">{{ h.yr }}</div>
                </div>
                <div style="flex:1">
                    <div class="cl-nm">{{ h.done }}/{{ h.total }} items completed</div>
                </div>
                <div class="cl-right">
                    <span :class="'pill ' + (h.total && h.done >= h.total ? 'p-done' : 'p-live')">
                        <svg-icon :icon="h.total && h.done >= h.total ? 'check2' : 'x'" />
                        {{ h.total && h.done >= h.total ? 'Complete' : 'Incomplete' }}
                    </span>
                </div>
            </div>
        </div>

        <checklist-log-detail v-if="adminEntryToShow" :entry="adminEntryToShow" @close="adminEntryToShow = null" />
        <checklist-view-as-employee-picker v-if="showViewAsPicker" @select="startViewingAs" @close="showViewAsPicker = false" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import SvgIcon from '@/components/SvgIcon.vue';
import ChecklistLogDetail from '@/components/ChecklistLogDetail.vue';
import ChecklistViewAsEmployeePicker from '@/components/ChecklistViewAsEmployeePicker.vue';

export default defineComponent({
    name: 'ChecklistView',
    props: [ 'checklistProp', 'slug' ],
    components: {
        SvgIcon,
        ChecklistLogDetail,
        ChecklistViewAsEmployeePicker
    },
    data() {
        return {
            checklistDateString: '',
            selectedDateString: '',
            history: [] as any[],
            todayHistory: null as any,
            checklist: null as any,
            checklistFull: null as any,
            adminHistory: [] as any[],
            adminEntryToShow: null as any,
            adminSearchQ: '',
            oneOffInput: '',
            viewAsTarget: null as any,
            showViewAsPicker: false,
            loaded: false
        }
    },
    computed: {
        isAdmin(): boolean {
            return !!this.$store.state.user.isAdmin || this.$store.state.user.teamHubEmployee?.IsManager;
        },
        readOnlyMode(): boolean {
            return !this.isToday || !!this.viewAsTarget;
        },
        isToday(): boolean {
            return this.selectedDateString == this.checklistDateString;
        },
        isPast(): boolean {
            return this.selectedDateString < this.checklistDateString;
        },
        isFuture(): boolean {
            return this.selectedDateString > this.checklistDateString;
        },
        totalCount(): number {
            return this.checklist.items.filter((i: any) => i.type != 'textblock').length
                + this.checklist.weeklyItems.filter((i: any) => i.type != 'textblock').length
                + this.checklist.oneOffItems.length;
        },
        doneCount(): number {
            return this.checklist.items.filter((i: any) => i.type != 'textblock' && i.checked).length
                + this.checklist.weeklyItems.filter((i: any) => i.type != 'textblock' && i.checked).length
                + this.checklist.oneOffItems.filter((i: any) => i.checked).length;
        },
        progressPct(): number {
            return this.totalCount ? Math.round(this.doneCount / this.totalCount * 100) : 0;
        },
        selectedDateLabel(): string {
            const d = this.parseDateString(this.selectedDateString);
            return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
        },
        weekDays(): any[] {
            const days = [];
            const monday = this.parseDateString(this.weekStartDateString(this.checklistDateString));
            for(let i = 0; i < 7; i++) {
                const d = new Date(monday);
                d.setDate(monday.getDate() + i);
                const dateString = this.formatDateString(d);
                days.push({
                    dateString,
                    dow: d.toLocaleDateString('en-US', { weekday: 'short' }),
                    dateNum: d.getDate(),
                    dotClass: this.dotClassForDate(dateString)
                });
            }
            return days;
        },
        historyEntries(): any[] {
            const earliest = this.weekDays.length ? this.weekDays[0].dateString : this.checklistDateString;
            return this.history
                .filter((h: any) => this.formatLogDate(h.Date) < earliest)
                .map((h: any) => {
                    const counts = this.countsFromJSON(h.ChecklistJSON);
                    const d = this.parseDateString(this.formatLogDate(h.Date));
                    return {
                        dateString: this.formatLogDate(h.Date),
                        mon: d.toLocaleDateString('en-US', { month: 'short' }),
                        day: d.getDate(),
                        yr: d.getFullYear(),
                        done: counts.done,
                        total: counts.total
                    }
                });
        },
        adminHistoryEntries(): any[] {
            return this.adminHistory
                .map((h: any) => {
                    const counts = this.countsFromJSON(h.ChecklistJSON);
                    const d = this.parseDateString(this.formatLogDate(h.Date));
                    return {
                        dateString: this.formatLogDate(h.Date),
                        mon: d.toLocaleDateString('en-US', { month: 'short' }),
                        day: d.getDate(),
                        yr: d.getFullYear(),
                        done: counts.done,
                        total: counts.total,
                        userName: h.UserName || 'Unknown user',
                        raw: h
                    }
                });
        },
        filteredAdminHistoryEntries(): any[] {
            if(this.adminSearchQ.length < 3) {
                return this.adminHistoryEntries;
            }
            const q = this.adminSearchQ.toLowerCase();
            return this.adminHistoryEntries.filter((h: any) => h.userName.toLowerCase().includes(q));
        }
    },
    async created() {
        this.checklist = JSON.parse(JSON.stringify(this.checklistProp));
        this.checklistFull = JSON.parse(JSON.stringify(this.checklistProp));
        if(!this.checklist.oneOffItems) {
            this.checklist.oneOffItems = [];
            this.checklistFull.oneOffItems = [];
        }
        this.checklistDateString = this.formatDateString(new Date());
        this.selectedDateString = this.checklistDateString;

        while(!this.$store.state.user.teamHubEmployeeLoaded) {
            await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Using a checklist needs an employee record (logs are per employee). Admins without one can still view it,
        // its log history and View as; checking off on their own is stopped by needsEmployee.
        if(!this.$store.state.user.teamHubEmployee && !this.$store.state.user.isAdmin) {
            alert('You do not have an employee record, which is required to use checklists');
            return;
        }

        await this.getHistory();
        if(this.isAdmin) {
            await this.getAdminHistory();
        }
        this.applyDay(this.checklistDateString);

        this.loaded = true;
    },
    methods: {
        formatDateString(date: Date): string {
            const year = date.getFullYear();
            let month = '' + (date.getMonth() + 1);
            if(month.length < 2) {
                month = '0' + month;
            }
            let day = '' + date.getDate();
            if(day.length < 2) {
                day = '0' + day;
            }
            return `${year}-${month}-${day}`;
        },
        parseDateString(s: string): Date {
            const parts = s.split('-').map((n: string) => parseInt(n));
            return new Date(parts[0], parts[1] - 1, parts[2]);
        },
        weekStartDateString(dateString: string): string {
            const d = this.parseDateString(dateString);
            const wd = (d.getDay() + 6) % 7;
            const monday = new Date(d);
            monday.setDate(d.getDate() - wd);
            return this.formatDateString(monday);
        },
        weekDatesFor(dateString: string): string[] {
            const start = this.parseDateString(this.weekStartDateString(dateString));
            const days = [];
            for(let i = 0; i < 7; i++) {
                const d = new Date(start);
                d.setDate(start.getDate() + i);
                days.push(this.formatDateString(d));
            }
            return days;
        },
        weeklyItemKey(item: any): string {
            return item.id || `${item.weekday}|${item.label}|${item.description}`;
        },
        wasWeeklyItemChecked(key: string, weekDates: string[], fromIndex: number, toIndexExclusive: number): boolean {
            for(let j = fromIndex; j < toIndexExclusive; j++) {
                const dayString = weekDates[j];
                if(dayString > this.checklistDateString) {
                    continue;
                }
                const dayRow = this.history.find((h: any) => this.formatLogDate(h.Date) == dayString);
                if(!dayRow) {
                    continue;
                }
                try {
                    const loggedWeekly = JSON.parse(dayRow.ChecklistJSON).weeklyItems || [];
                    const loggedItem = loggedWeekly.find((li: any) => this.weeklyItemKey(li) == key);
                    if(loggedItem && loggedItem.checked) {
                        return true;
                    }
                } catch(e) {
                    // ignore malformed log entry
                }
            }
            return false;
        },
        wasOneOffItemChecked(id: string, weekDates: string[], toIndexExclusive: number): boolean {
            // One-off items have no fixed weekday, so scan the whole week-so-far — days before
            // the item existed simply won't have it in their log, so they're a harmless no-op.
            for(let j = 0; j < toIndexExclusive; j++) {
                const dayString = weekDates[j];
                if(dayString > this.checklistDateString) {
                    continue;
                }
                const dayRow = this.history.find((h: any) => this.formatLogDate(h.Date) == dayString);
                if(!dayRow) {
                    continue;
                }
                try {
                    const loggedOneOff = JSON.parse(dayRow.ChecklistJSON).oneOffItems || [];
                    const loggedItem = loggedOneOff.find((li: any) => li.id == id);
                    if(loggedItem && loggedItem.checked) {
                        return true;
                    }
                } catch(e) {
                    // ignore malformed log entry
                }
            }
            return false;
        },
        formatLogDate(dateVal: any): string {
            const d = new Date(dateVal);
            const year = d.getUTCFullYear();
            let month = '' + (d.getUTCMonth() + 1);
            if(month.length < 2) {
                month = '0' + month;
            }
            let day = '' + d.getUTCDate();
            if(day.length < 2) {
                day = '0' + day;
            }
            return `${year}-${month}-${day}`;
        },
        countsFromJSON(jsonStr: string): any {
            try {
                const parsed = JSON.parse(jsonStr);
                const items = (parsed.items || []).filter((i: any) => i.type != 'textblock');
                const weekly = (parsed.weeklyItems || []).filter((i: any) => i.type != 'textblock');
                const oneOff = parsed.oneOffItems || [];
                return {
                    done: items.filter((i: any) => i.checked).length + weekly.filter((i: any) => i.checked).length + oneOff.filter((i: any) => i.checked).length,
                    total: items.length + weekly.length + oneOff.length
                }
            } catch(e) {
                return { done: 0, total: 0 };
            }
        },
        dotClassForDate(dateString: string): string {
            const row = this.history.find((h: any) => this.formatLogDate(h.Date) == dateString);
            let counts = { done: 0, total: 0 };
            if(row?.ChecklistJSON) {
                counts = this.countsFromJSON(row.ChecklistJSON);
            }
            if(dateString == this.checklistDateString) {
                if(counts.done >= counts.total) {
                    return 'dot-done';
                } else {
                    return 'dot-today';
                }
            }
            if(dateString > this.checklistDateString) {
                return 'dot-up';
            }            
            if(!counts.total) {
                return '';
            }
            if(counts.done >= counts.total) {
                return 'dot-done';
            }
            if(counts.done > 0) {
                return 'dot-part';
            }
            return '';
        },
        applyDay(dateString: string) {
            const row = this.history.find((h: any) => this.formatLogDate(h.Date) == dateString);
            if(row) {
                const logChecklist = JSON.parse(row.ChecklistJSON);
                console.log(logChecklist)
                logChecklist.oneOffItems = logChecklist.oneOffItems || [];
                this.checklist = logChecklist;
            } else {
                this.checklist = JSON.parse(JSON.stringify(this.checklistFull));
                this.checklist.oneOffItems = [];
                const targetWeekday = new Date(dateString).getUTCDay();
                const weekDates = this.weekDatesFor(dateString);
                const targetIndex = weekDates.indexOf(dateString);
                const seenKeys = new Set<string>();
                const weeklyItems: any[] = [];

                // Carry forward any earlier-this-week items that weren't checked off on their assigned day
                // (or on any later day this week they may have already been carried into).
                for(let i = 0; i < targetIndex; i++) {
                    const dayString = weekDates[i];
                    if(dayString > this.checklistDateString) {
                        continue; // that day hasn't happened yet, nothing to carry forward
                    }
                    const dow = new Date(dayString).getUTCDay();
                    for(const item of this.checklistFull.weeklyItems) {
                        if(item.type == 'textblock' || item.weekday != dow) {
                            continue;
                        }
                        const key = this.weeklyItemKey(item);
                        if(seenKeys.has(key)) {
                            continue;
                        }
                        seenKeys.add(key);
                        if(!this.wasWeeklyItemChecked(key, weekDates, i, targetIndex)) {
                            const carried = JSON.parse(JSON.stringify(item));
                            carried.checked = false;
                            carried.carriedFromLabel = this.parseDateString(dayString).toLocaleDateString('en-US', { weekday: 'short' });
                            weeklyItems.push(carried);
                        }
                    }
                }

                // This day's own scheduled items.
                for(const item of this.checklistFull.weeklyItems) {
                    if(item.weekday != targetWeekday) {
                        continue;
                    }
                    const key = this.weeklyItemKey(item);
                    if(seenKeys.has(key)) {
                        continue;
                    }
                    seenKeys.add(key);
                    const fresh = JSON.parse(JSON.stringify(item));
                    fresh.checked = false;
                    weeklyItems.push(fresh);
                }

                this.checklist.weeklyItems = weeklyItems;

                // Carry forward any one-off items added earlier this week that haven't been checked off yet
                // (whether still sitting on the day they were added, or already carried into a later day).
                const oneOffMap = new Map<string, any>();
                for(let i = 0; i < targetIndex; i++) {
                    const dayString = weekDates[i];
                    if(dayString > this.checklistDateString) {
                        continue;
                    }
                    const dayRow = this.history.find((h: any) => this.formatLogDate(h.Date) == dayString);
                    if(!dayRow) {
                        continue;
                    }
                    try {
                        const loggedOneOff = JSON.parse(dayRow.ChecklistJSON).oneOffItems || [];
                        for(const item of loggedOneOff) {
                            if(!oneOffMap.has(item.id)) {
                                oneOffMap.set(item.id, item);
                            }
                        }
                    } catch(e) {
                        // ignore malformed log entry
                    }
                }
                for(const item of oneOffMap.values()) {
                    if(!this.wasOneOffItemChecked(item.id, weekDates, targetIndex)) {
                        const carried = JSON.parse(JSON.stringify(item));
                        carried.checked = false;
                        carried.carriedFromLabel = this.parseDateString(item.addedDate || weekDates[0]).toLocaleDateString('en-US', { weekday: 'short' });
                        this.checklist.oneOffItems.push(carried);
                    }
                }

                for(const item of this.checklist.items) {
                    item.checked = false;
                }
            }
        },
        selectDay(dateString: string) {
            this.selectedDateString = dateString;
            this.applyDay(dateString);
        },
        // An admin with no employee record viewing their own checklist (View as someone else still works)
        needsEmployee(): boolean {
            if(this.viewAsTarget || this.$store.state.user.teamHubEmployee) {
                return false;
            }
            alert('You cannot use checklists until you have an employee record created.');
            return true;
        },
        toggleItem(item: any) {
            if(!this.isToday || this.viewAsTarget || this.needsEmployee()) {
                return;
            }
            item.checked = !item.checked;
            this.checklistChange();
        },
        addOneOffItem() {
            const label = this.oneOffInput.trim();
            if((!this.isToday && !this.isFuture) || !label || this.needsEmployee()) {
                return;
            }
            const item: any = { id: uuidv4(), label, checked: false, addedDate: this.selectedDateString };
            if(this.viewAsTarget) {
                // Adding on someone else's behalf — tag it so it's clear who actually added it.
                item.addedByName = `${this.$store.state.user.firstName} ${this.$store.state.user.lastName}`;
            }
            this.checklist.oneOffItems.push(item);
            this.oneOffInput = '';
            this.checklistChange();
        },
        removeOneOffItem(item: any) {
            if(!this.isToday || this.viewAsTarget) {
                return;
            }
            const i = this.checklist.oneOffItems.indexOf(item);
            if(i > -1) {
                this.checklist.oneOffItems.splice(i, 1);
                this.checklistChange();
            }
        },
        async getHistory() {
            // No employee record: no history of your own to load
            if(!this.viewAsTarget && !this.$store.state.user.teamHubEmployee) {
                this.history = [];
                return;
            }
            let url = `${this.$store.state.apiUrl}/api/employees/checklistloghistory?slug=${this.slug}&limit=30`;
            if(this.viewAsTarget) {
                url += `&targetEmployeeID=${this.viewAsTarget.targetEmployeeID}`;
            }
            await axios.get(url)
                .then(resp => {
                    this.history = resp.data;
                })
                .catch(e => {
                    this.history = [];
                })
        },
        async getAdminHistory() {
            await axios.get(`${this.$store.state.apiUrl}/api/employees/allchecklistloghistory/${this.slug}?limit=200`)
                .then(resp => {
                    this.adminHistory = resp.data;
                    console.log('here');
                    console.log(this.adminHistory);
                })
                .catch(e => {
                    this.adminHistory = [];
                })
        },
        showAdminEntry(entry: any) {
            this.adminEntryToShow = entry;
        },
        async startViewingAs(target: any) {
            this.viewAsTarget = target;
            this.showViewAsPicker = false;
            this.selectedDateString = this.checklistDateString;
            await this.getHistory();
            this.applyDay(this.checklistDateString);
        },
        async stopViewingAs() {
            this.viewAsTarget = null;
            this.selectedDateString = this.checklistDateString;
            await this.getHistory();
            this.applyDay(this.checklistDateString);
        },
        async checklistChange() {
            // Use the day being viewed, not necessarily today — one-off items can be
            // added ahead of time on future days, which logs against that future date.
            const data: any = {
                checklist: this.checklist,
                slug: this.slug,
                date: this.selectedDateString
            }
            if(this.viewAsTarget) {
                data.targetEmployeeID = this.viewAsTarget.targetEmployeeID;
            }
            await axios.post(`${this.$store.state.apiUrl}/api/employees/checklistlog`, data)
                .then(() => {
                    const json = JSON.stringify(this.checklist);
                    const existing = this.history.find((h: any) => this.formatLogDate(h.Date) == this.selectedDateString);
                    if(existing) {
                        existing.ChecklistJSON = json;
                    } else {
                        this.history.unshift({ Date: this.selectedDateString, ChecklistJSON: json });
                    }
                })
                .catch(e => {
                    console.log(e);
                })
        }
    }
});
</script>
